/**
 * BIRHAUS Performance Budgets
 * 
 * These constants define the performance and cognitive load limits
 * that all BIRHAUS components must adhere to.
 */

export const BIRHAUS_PERFORMANCE = {
  /**
   * Bundle and asset size limits (in bytes)
   */
  BUDGETS: {
    MAX_BUNDLE_SIZE: 200_000,        // 200KB compressed total bundle
    MAX_COMPONENT_SIZE: 50_000,      // 50KB per individual component
    MAX_THEME_SIZE: 25_000,          // 25KB per theme
    MAX_ICON_SIZE: 2_000,            // 2KB per SVG icon
    MAX_IMAGE_SIZE: 100_000,         // 100KB per image
    MAX_FONT_SIZE: 50_000,           // 50KB per font file
  },

  /**
   * Core Web Vitals and performance metrics
   */
  WEB_VITALS: {
    MAX_TTI: 2000,                   // Time to Interactive (ms)
    MAX_FCP: 1000,                   // First Contentful Paint (ms)
    MAX_LCP: 2500,                   // Largest Contentful Paint (ms)
    MAX_CLS: 0.1,                    // Cumulative Layout Shift
    MAX_INP: 200,                    // Interaction to Next Paint (ms)
    MAX_TBT: 300,                    // Total Blocking Time (ms)
    MIN_FID: 100,                    // First Input Delay (ms)
  },

  /**
   * Cognitive load and UX performance limits
   */
  COGNITIVE: {
    MAX_ANIMATION_DURATION: 300,     // Maximum animation time (ms)
    MIN_ANIMATION_DURATION: 150,     // Minimum for perceived responsiveness
    MAX_TRANSITION_DURATION: 200,    // CSS transition duration
    MIN_TOUCH_TARGET: 44,            // Minimum touch target size (px)
    MIN_CONTRAST_RATIO: 4.5,         // WCAG AA compliance
    MAX_SIMULTANEOUS_ANIMATIONS: 2,  // Prevent cognitive overload
    MAX_MODAL_STACK: 2,              // Maximum nested modals
    MAX_TOAST_STACK: 3,              // Maximum concurrent notifications
  },

  /**
   * Network and loading performance
   */
  NETWORK: {
    MAX_API_RESPONSE_TIME: 1000,     // API response time (ms)
    MAX_CONCURRENT_REQUESTS: 6,      // Simultaneous network requests
    MAX_RETRY_ATTEMPTS: 3,           // API retry limit
    CACHE_TTL: 300_000,              // 5 minutes cache TTL
    OFFLINE_CACHE_SIZE: 50_000_000,  // 50MB offline cache limit
  },

  /**
   * Memory and resource usage
   */
  MEMORY: {
    MAX_DOM_NODES: 1500,             // Maximum DOM elements
    MAX_EVENT_LISTENERS: 100,        // Event listener limit
    MAX_COMPONENT_INSTANCES: 50,     // Per component type
    MAX_STATE_SIZE: 1_000_000,       // 1MB state object size
    MEMORY_LEAK_THRESHOLD: 10_000_000, // 10MB memory leak threshold
  },

  /**
   * Accessibility performance
   */
  ACCESSIBILITY: {
    MAX_TAB_STOPS: 20,               // Maximum tab stops per view
    MIN_FOCUS_INDICATOR: 2,          // Minimum focus outline width (px)
    MAX_HEADING_LEVELS: 6,           // H1-H6 semantic structure
    MIN_COLOR_CONTRAST: 4.5,         // Text contrast ratio
    MIN_COLOR_CONTRAST_LARGE: 3.0,   // Large text contrast ratio
    MAX_ALT_TEXT_LENGTH: 125,        // Alt text character limit
  }
} as const

/**
 * Performance monitoring and alerting thresholds
 */
export const BIRHAUS_ALERTS = {
  /**
   * Warning thresholds (80% of limits)
   */
  WARNING: {
    BUNDLE_SIZE: Math.floor(BIRHAUS_PERFORMANCE.BUDGETS.MAX_BUNDLE_SIZE * 0.8),
    TTI: Math.floor(BIRHAUS_PERFORMANCE.WEB_VITALS.MAX_TTI * 0.8),
    DOM_NODES: Math.floor(BIRHAUS_PERFORMANCE.MEMORY.MAX_DOM_NODES * 0.8),
    API_RESPONSE: Math.floor(BIRHAUS_PERFORMANCE.NETWORK.MAX_API_RESPONSE_TIME * 0.8),
  },

  /**
   * Critical thresholds (95% of limits)
   */
  CRITICAL: {
    BUNDLE_SIZE: Math.floor(BIRHAUS_PERFORMANCE.BUDGETS.MAX_BUNDLE_SIZE * 0.95),
    TTI: Math.floor(BIRHAUS_PERFORMANCE.WEB_VITALS.MAX_TTI * 0.95),
    DOM_NODES: Math.floor(BIRHAUS_PERFORMANCE.MEMORY.MAX_DOM_NODES * 0.95),
    API_RESPONSE: Math.floor(BIRHAUS_PERFORMANCE.NETWORK.MAX_API_RESPONSE_TIME * 0.95),
  }
} as const

/**
 * Lighthouse scoring weights for Birhaus Score calculation
 */
export const BIRHAUS_SCORING = {
  WEIGHTS: {
    PERFORMANCE: 0.4,                // 40% of total score
    ACCESSIBILITY: 0.3,              // 30% of total score
    COGNITIVE_LOAD: 0.2,             // 20% of total score (Miller's Law, etc.)
    SPANISH_COVERAGE: 0.1,           // 10% of total score
  },
  
  PENALTIES: {
    CONFIRMATION_DIALOG: -10,        // -10 points per confirmation dialog
    MILLER_LAW_VIOLATION: -5,        // -5 points per Miller's Law violation
    MISSING_SPANISH_LABEL: -2,       // -2 points per missing Spanish label
    ACCESSIBILITY_VIOLATION: -3,     // -3 points per a11y violation
    PERFORMANCE_BUDGET_EXCEEDED: -5, // -5 points per budget violation
  },

  BONUSES: {
    UNDO_PATTERN_IMPLEMENTED: 5,     // +5 points per undo pattern
    PROGRESSIVE_DISCLOSURE: 3,       // +3 points per progressive disclosure
    SPANISH_FIRST_DESIGN: 2,         // +2 points for Spanish-first approach
    WCAG_AAA_COMPLIANCE: 5,          // +5 points for exceeding WCAG AA
    PERFORMANCE_EXCELLENCE: 3,       // +3 points for exceeding performance budgets
  }
} as const

import type { CognitiveLoadMetrics } from '@birhaus/types/metrics'

/**
 * Type definitions for performance monitoring
 */
export interface PerformanceMetrics {
  bundleSize: number
  componentSize: number
  ttiduration: number
  fcp: number
  lcp: number
  cls: number
  inp: number
  domNodes: number
  memoryUsage: number
  apiResponseTime: number
  accessibilityScore: number
}

export type { CognitiveLoadMetrics }

export interface BirhausScore {
  total: number                      // 0-100 total score
  performance: number                // Performance subscore
  accessibility: number             // Accessibility subscore
  cognitiveLoad: number             // Cognitive load subscore
  spanishCoverage: number           // Spanish coverage subscore
  violations: string[]              // List of violations
  recommendations: string[]          // Improvement recommendations
  timestamp: Date                   // When score was calculated
}

/**
 * Default Birhaus performance configuration
 */
export const DEFAULT_BIRHAUS_CONFIG = {
  strictMode: true,
  cognitiveLoadTracking: true,
  performanceBudgets: true,
  accessibilityValidation: true,
  spanishFirst: true,
  undoOverConfirm: true,
  millerLawEnforcement: true,
  progressiveDisclosure: true,
} as const

/**
 * Environment-specific configurations
 */
export const BIRHAUS_ENV_CONFIG = {
  development: {
    ...DEFAULT_BIRHAUS_CONFIG,
    strictMode: true,
    showWarnings: true,
    detailedLogging: true,
    performanceMonitoring: true,
  },
  
  production: {
    ...DEFAULT_BIRHAUS_CONFIG,
    strictMode: false,
    showWarnings: false,
    detailedLogging: false,
    performanceMonitoring: true,
    analyticsEnabled: true,
  },
  
  testing: {
    ...DEFAULT_BIRHAUS_CONFIG,
    strictMode: true,
    showWarnings: false,
    detailedLogging: false,
    performanceMonitoring: false,
  }
} as const